import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);
export const useSettings = () => useContext(SettingsContext);

const DEFAULTS = {
  brand_name: "CareBed",
  hero_title: "Find an open bed for your loved one, faster.",
  hero_subtitle: "Search licensed assisted living and nursing facilities across Colorado. See availability, compare, and reach out — always free for families and hospital discharge planners.",
  hero_cta: "Find care now",
  for_fac_title: "Reach families the moment they're searching.",
  for_fac_subtitle: "Claim your free listing, then upgrade to put your photos, amenities, and live bed availability in front of families and hospital discharge planners across Colorado.",
  color_primary: "#1A365D",
  color_primary_hover: "#12283F",
  color_secondary: "#0077B6",
  color_accent: "#E3F3ED",
  hero_image: "",
  for_fac_image: "",
  card_show_licensed_beds: true,
  card_show_type_detail: true,
  card_show_availability: true,
  card_show_location: true,
  card_show_phone: true,
  facility_categories: [
    { key: "assisted_living", label: "Assisted Living", enabled: true },
    { key: "nursing", label: "Nursing Facility", enabled: true },
  ],
  about_title: "A senior-care directory built for families — not for commissions.",
  about_intro: "Finding care for someone you love is stressful enough without wondering whether the \"advisor\" helping you is really working for you. CareBed takes a different approach: we're an open, transparent directory of licensed senior-care facilities, free for families, with no hidden incentives.",
  about_money_body: "We keep it simple and out in the open. CareBed is free for families. Facilities can claim their listing for free, and may choose to pay a flat, published monthly subscription to add photos, amenities, live bed availability, and better visibility. We also offer an optional one-time \"done-for-you\" setup service for facilities that want help getting started. That's it — we are not paid a commission or \"placement fee\" when a resident moves in. Our revenue doesn't depend on steering you toward any particular facility.",
  about_transparency_intro: "Many senior-living \"referral\" and \"advisor\" services are free to families for a reason: the facilities pay them a large commission — often a month or more of rent — every time a family they refer moves in. That creates a hidden incentive to steer families toward whoever pays the most, and to only show you the facilities that have agreed to pay. You rarely get to see the full picture.",
  about_how_title: "How CareBed works",
  about_money_title: "How we make money",
  about_transparency_title: "Why we're more transparent",
  about_contact_title: "Contact us",
  about_contact_intro: "Questions, feedback, or a facility that needs updating? We'd love to hear from you. Send a note and a real person will reply.",
  ff_plans_title: "Listing plans",
  ff_plans_subtitle: "Free forever, or go Premium for a price that fits your community's type and size. Upgrade or cancel anytime.",
  ff_matrix_title: "Premium pricing by facility type & size",
  plan_free_name: "Free",
  plan_premium_name: "Premium",
  payment_type_options: [
    "Private Pay", "Medicaid", "VA Benefits",
    "Long-Term Care Insurance", "PACE", "Case-by-Case/Specialized Waivers",
  ],
};

function applyBrandColors(s) {
  const css = `
    .bg-\\[\\#1A365D\\]{background-color:${s.color_primary}!important}
    .hover\\:bg-\\[\\#12283F\\]:hover{background-color:${s.color_primary_hover}!important}
    .text-\\[\\#1A365D\\]{color:${s.color_primary}!important}
    .border-\\[\\#1A365D\\]{border-color:${s.color_primary}!important}
    .ring-\\[\\#1A365D\\]\\/30{--tw-ring-color:${s.color_primary}4d!important}
    .bg-\\[\\#0077B6\\]{background-color:${s.color_secondary}!important}
    .text-\\[\\#0077B6\\]{color:${s.color_secondary}!important}
    .border-\\[\\#0077B6\\]{border-color:${s.color_secondary}!important}
    .fill-\\[\\#0077B6\\]{fill:${s.color_secondary}!important}
    .ring-\\[\\#0077B6\\]\\/30{--tw-ring-color:${s.color_secondary}4d!important}
    .bg-\\[\\#E3F3ED\\]{background-color:${s.color_accent}!important}
  `;
  let tag = document.getElementById("brand-overrides");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "brand-overrides";
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      applyBrandColors(data);
    } catch (e) {
      console.error("Failed to load site settings", e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ settings, refresh, setSettings }), [settings, refresh]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}
